import { Element, useEditor, useNode, type NodeId, type NodeTree } from '@craftjs/core';
import Container from '.';
import Button from '../Button';
import Text from '../Text';
import Image from '../Image';
import Video from '../Video';
import { ButtonStack } from '@/components/canvas/ButtonStack/ButtonStack';
import {
  BUTTON_NODE_NAME,
  CONTENT_BLOCK_HEIGHT,
  CONTENT_BLOCK_NAME,
  IMAGE_NODE_HEIGHT,
  IMAGE_NODE_NAME,
  TEXT_NODE_NAME,
  VIDEO_NODE_NAME,
  VIDEO_NODE_HEIGHT,
  DUPLICATE_EVENT,
} from '@/utils/constants';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import { selectPreviewModeEnabled } from '@/store/system/selectors';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setActivePanel } from '@/store/assetsPanel/assetsPanelSlice';
import { selectActivePanel } from '@/store/assetsPanel/selector';
import { setDefaultActiveCategoryId } from '@/store/section/slice';
import {
  getShouldBlockStylePanelClose,
  setPendingStylePanelAction,
  STYLE_PANEL_UNSAVED_TOAST,
} from '@/utils/stylePanelGuard';
import { showToast } from '@/store/notifications/slice';
import { useInspirationPages } from '@/hooks/useInspirationPages';
import { ASSETS_PANEL, SECTION_CATEGORIES } from '@builder4/shared/utils/constants';
import { useMixpanel } from '@/hooks/useMixpanel';
import { EVENT } from '@/analytics/mixpanel';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import type { Breakpoint } from '@builder4/shared/types/layout';
import { getBreakpointProp, setBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import { type ContainerProps } from '@builder4/shared/components/Container';
import { isRelativelyFillableElementV2, isSingleRootContainer } from '@/utils/helperFunc';
import { getBorderRadiusStyle } from '@builder4/shared/utils/styleHelpers';

function OptionPlaceholder({
  placeholderHeight,
  className,
}: {
  placeholderHeight: string;
  className?: string;
}) {
  const previewMode = useAppSelector(selectPreviewModeEnabled);
  const dispatch = useAppDispatch();
  const { actions, query } = useEditor();
  const {
    id,
    nodeName,
    props: nodeProps,
    node: currentNode,
  } = useNode((node) => {
    return {
      nodeName: node.data.displayName,
      props: node.data.props as ContainerProps,
      node,
    };
  });

  const isOnlyRootContainer = currentNode ? isSingleRootContainer(currentNode, query) : false;

  // Get breakpoint-aware prop functions
  const { getBPProp } = useBreakpointAwareProp(nodeName);

  // Read props through breakpoint-aware system
  const flexDirection = getBPProp(nodeProps, 'flexDirection');
  const backgroundColor = getBPProp(nodeProps, 'backgroundColor');
  const borderRadius = getBPProp(nodeProps, 'borderRadius');
  const { getInitialPlaceholderHeight } = useCraftHelper();
  const { openInspirationPages } = useInspirationPages();
  const activePanel = useAppSelector(selectActivePanel);

  const requestPanel = (panel: string, beforeNavigate?: () => void) => {
    const isStyleEditorOpen =
      activePanel === ASSETS_PANEL.STYLES_EDIT || activePanel === ASSETS_PANEL.STYLES_NEW;
    if (isStyleEditorOpen && panel !== activePanel && getShouldBlockStylePanelClose()) {
      setPendingStylePanelAction(panel);
      dispatch(showToast(STYLE_PANEL_UNSAVED_TOAST));
      return;
    }
    beforeNavigate?.();
    dispatch(setActivePanel(panel));
  };
  const track = useMixpanel();

  const handleClick = (elementName: string) => {
    const baseFlexDirection = getBreakpointProp(nodeProps, 'flexDirection', 'desktop');
    const newNodeTrees: NodeTree[] = [];
    if (elementName === CONTENT_BLOCK_NAME) {
      const placeholderHeight = getInitialPlaceholderHeight(id) || CONTENT_BLOCK_HEIGHT;
      newNodeTrees.push(
        query
          .parseReactElement(
            <Element
              canvas={true}
              is={Container}
              placeholderHeight={placeholderHeight}
              fillSpace={baseFlexDirection === 'column' ? 'no' : 'yes'}
            />,
          )
          .toNodeTree(),
      );
    } else if (elementName === BUTTON_NODE_NAME) {
      newNodeTrees.push(query.parseReactElement(<Element is={Button} />).toNodeTree());
    } else if (elementName === TEXT_NODE_NAME) {
      newNodeTrees.push(
        query
          .parseReactElement(
            <Element is={Text} fillSpace={baseFlexDirection === 'column' ? 'no' : 'yes'} />,
          )
          .toNodeTree(),
      );
    } else if (elementName === IMAGE_NODE_NAME) {
      const placeholderHeight = getInitialPlaceholderHeight(id) || IMAGE_NODE_HEIGHT;
      newNodeTrees.push(
        query
          .parseReactElement(
            <Element
              height={`${placeholderHeight}px`}
              is={Image}
              fillSpace={baseFlexDirection === 'column' ? 'no' : 'yes'}
            />,
          )
          .toNodeTree(),
      );
    } else if (elementName === VIDEO_NODE_NAME) {
      const placeholderHeight = getInitialPlaceholderHeight(id) || VIDEO_NODE_HEIGHT;
      newNodeTrees.push(
        query
          .parseReactElement(
            <Element
              height={`${placeholderHeight}px`}
              is={Video}
              fillSpace={baseFlexDirection === 'column' ? 'no' : 'yes'}
            />,
          )
          .toNodeTree(),
      );
    } else if (elementName === 'ROW_CONTAINERS' || elementName === 'COLUMN_CONTAINERS') {
      const placeholderHeight = getInitialPlaceholderHeight(id) || CONTENT_BLOCK_HEIGHT;
      const width = elementName === 'COLUMN_CONTAINERS' ? '50%' : '100%';
      newNodeTrees.push(
        query
          .parseReactElement(
            <Element
              canvas={true}
              is={Container}
              placeholderHeight={placeholderHeight}
              width={width}
              flexBasis={width}
              fillSpace={baseFlexDirection === 'column' ? 'no' : 'yes'}
            />,
          )
          .toNodeTree(),
      );
      newNodeTrees.push(
        query
          .parseReactElement(
            <Element
              canvas={true}
              is={Container}
              placeholderHeight={placeholderHeight}
              width={width}
              flexBasis={width}
              fillSpace={baseFlexDirection === 'column' ? 'no' : 'yes'}
            />,
          )
          .toNodeTree(),
      );
    } else if (elementName === 'HERO_SECTION') {
      requestPanel('Sections', () =>
        dispatch(setDefaultActiveCategoryId(SECTION_CATEGORIES.hero.value)),
      );
      return;
    } else if (elementName === 'INSPIRATION') {
      track(EVENT.OPENED_INSPIRATION_GALLERY);
      openInspirationPages();
      return;
    } else if (elementName === 'EXPLORE_MORE') {
      requestPanel('Sections', () => dispatch(setDefaultActiveCategoryId('-1')));
      return;
    } else if (elementName === 'All') {
      requestPanel('Elements');
      return;
    }

    if (newNodeTrees.length > 0) {
      newNodeTrees.forEach((newNodeTree, index) => {
        const method = index === 0 ? actions : actions.history.merge();
        method.addNodeTree({ ...newNodeTree, eventSource: DUPLICATE_EVENT }, id);
      });

      ['desktop', 'tablet', 'mobile'].forEach((bp) => {
        newNodeTrees.forEach((newNodeTree) => {
          const newNode = query.node(newNodeTree.rootNodeId as NodeId).get();
          if (!newNode) return;
          const parentFlexDirection = getBreakpointProp(
            nodeProps,
            'flexDirection',
            bp as Breakpoint,
          );
          actions.history
            .merge()
            .setProp(newNodeTree.rootNodeId as string, (props: Partial<ContainerProps>) => {
              if (isRelativelyFillableElementV2(newNode, bp as Breakpoint)) {
                setBreakpointProp(
                  props,
                  'fillSpace',
                  parentFlexDirection === 'column' ? 'no' : 'yes',
                  bp as Breakpoint,
                  newNode.data.displayName,
                );
              }
            });
        });
      });

      actions.selectNode(newNodeTrees[newNodeTrees.length - 1].rootNodeId);
    }
  };

  if (previewMode) return null;
  return (
    <ButtonStack
      className={`max-h-full w-full ${className} ${backgroundColor ? '' : 'bg-indigo-500/10'}`}
      styles={{
        height: placeholderHeight,
        ...getBorderRadiusStyle(borderRadius || [0, 0, 0, 0]),
      }}
      onSelect={handleClick}
      flexDirection={flexDirection}
      isOnlyRootContainer={isOnlyRootContainer}
    />
  );
}

export default OptionPlaceholder;
